import { bool, func } from "prop-types";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { createLabelQuery, removeLabelQuery, updateLabelGroupQuery, updateLabelQuery } from "@query";
import { LabelGroups, MAX_LABEL_LENGTH } from "@utils";
import { useEstimateEditorSettings, useEstimateEditorStore, useEstimateStructure } from "@tools";
import { useAdditionalColors, useEditorWebsocket, useStores } from "@hooks";
import { Dialog, Button, InfoLabel, LabelList } from "@components";
import { Grid } from "@material-ui/core";
import { TitleRow } from "@tools/EstimateEditor/dialogs/LabelsDialog/components/TitleRow";
import { LabelRow } from "@components/LabelEditor/components/LabelRow";
import { NewLabelRow } from "@tools/EstimateEditor/dialogs/LabelsDialog/components/NewLabelRow";

export const LabelsDialog = ({
  open,
  onClose,
  onCreateLabel,
  onUpdateLabel,
}) => {
  const { t } = useTranslation();
  const { estimateUuid } = useParams();
  const editorStore = useEstimateEditorStore();
  const { userStore } = useStores();
  const socket = useEditorWebsocket();
  const { isTemplate } = useEstimateEditorSettings();
  const structure = useEstimateStructure();
  
  const { companyLabels, customLabels } = editorStore;
  const { isManager } = userStore;
  
  const additionalColorProps = useAdditionalColors([...companyLabels, ...customLabels]);
  
  const handleCreateLabel = (custom) => async (name, backgroundColor, fontColor) => {
    const id = await createLabelQuery(name, backgroundColor, fontColor, custom ? estimateUuid : LabelGroups.LABELS);
    const label = { id, name, backgroundColor, fontColor };
    if(custom) {
      editorStore.addCustomLabel(label);
      socket.requestEstimateLabelCreate(label);
    } else {
      editorStore.addCompanyLabel(label);
      socket.requestCompanyLabelCreate(label);
    }
    onCreateLabel(label);
    return id;
  }
  
  const handleRemoveCompanyLabel = async (id) => {
    await updateLabelGroupQuery(id, LabelGroups.UNLISTED);
    socket.requestCompanyLabelRemove(id);
    const label = editorStore.removeCompanyLabel(id);
    editorStore.addCustomLabel(label);
  }
  
  const handleRemoveCustomLabel = async (id, isUnlisted) => {
    if(!isUnlisted)
      await removeLabelQuery(id);
    editorStore.removeCustomLabel(id);
    socket.requestEstimateLabelRemove(id);
    structure.purgeLabel(id);
  }
  
  const handleUpdateLabel = (custom) => async (id, name, backgroundColor, fontColor) => {
    await updateLabelQuery(id, name, backgroundColor, fontColor);
    const label = { id, name, backgroundColor, fontColor };
    socket.requestLabelUpdate(id, label, custom);
    if(custom)
      editorStore.updateCustomLabel(label.id, label);
    else
      editorStore.updateCompanyLabel(label.id, label);
    onUpdateLabel(label);
  }
  
  return <Dialog
    open={open}
    width={520}
    title={t("views.settings.menu_full.labels")}
    onClose={onClose}
    actions={
      <Button
        variant="contained"
        onClick={onClose}
      >
        {t("common.close")}
      </Button>
    }
  >
    <InfoLabel label={t("views.editor.labels.company")} />
    {
      isManager
        ? <>
          <TitleRow />
          {
            companyLabels.map((label) => (
              <LabelRow
                key={label.id}
                onRemove={handleRemoveCompanyLabel}
                onUpdate={handleUpdateLabel(false)}
                maxLength={MAX_LABEL_LENGTH}
                {...additionalColorProps}
                {...label}
              />
            ))
          }
          <NewLabelRow
            onCreate={handleCreateLabel(false)}
            {...additionalColorProps}
          />
        </>
        : <Grid item container>
          <LabelList labels={companyLabels} spacing="large" />
        </Grid>
    }
    <InfoLabel label={t(`views.editor.labels.${isTemplate ? "template" : "project"}`)} />
    <TitleRow />
    {
      customLabels.map((label) => (
        <LabelRow
          key={label.id}
          onRemove={handleRemoveCustomLabel}
          onUpdate={handleUpdateLabel(true)}
          maxLength={MAX_LABEL_LENGTH}
          {...additionalColorProps}
          {...label}
        />
      ))
    }
    <NewLabelRow
      onCreate={handleCreateLabel(true)}
      {...additionalColorProps}
    />
  </Dialog>;
}

LabelsDialog.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  onCreateLabel: func.isRequired,
  onUpdateLabel: func.isRequired,
}